import { type ServiceParams, unwrapJson } from "../client";
import type {
  CreateSignupKeyRequest,
  SignupKeyCreatedResponse,
  SignupKeyListFilters,
  SignupKeyOut,
} from "./types";

/**
 * Signup-keys service — wraps ``/v1/admin/signup-keys``. The list
 * endpoint returns a bare array (not the envelope used by other
 * admin surfaces); the FE-4 Users tab calls it once + filters
 * client-side so paginating is not strictly necessary at pilot
 * scale.
 *
 * Create response includes the plaintext ``code`` shown ONCE — the
 * FE must surface it in a show-once dialog and never store it.
 */
const SignupKeys = ({ url, config }: ServiceParams) => {
  const base = `${url}/v1/admin/signup-keys`;

  const buildQuery = (filters?: Record<string, unknown>) => {
    const params = new URLSearchParams();
    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      }
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  };

  return {
    async create(
      payload: CreateSignupKeyRequest = {}
    ): Promise<SignupKeyCreatedResponse> {
      const response = await fetch(base, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<SignupKeyCreatedResponse>(response);
    },

    async list(
      filters: SignupKeyListFilters = {}
    ): Promise<SignupKeyOut[]> {
      const response = await fetch(
        `${base}${buildQuery(filters as Record<string, unknown>)}`,
        { method: "GET", headers: config() }
      );
      return unwrapJson<SignupKeyOut[]>(response);
    },

    async revoke(keyId: string): Promise<void> {
      const response = await fetch(`${base}/${keyId}`, {
        method: "DELETE",
        headers: config(),
      });
      if (!response.ok) return unwrapJson<void>(response);
    },
  };
};

export default SignupKeys;
